import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Pagination } from "antd";
import { Calendar, Download, Edit2, Info, Trash } from "lucide-react";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { getEmployeeId } from "@/lib/storeManager";
import {
  PtsLeaveApprovalService,
  type LeaveApprovalItem,
  type LeaveType,
} from "@/services/APIs";
import { isPTSAdmin } from "../../utils/rolePermissions";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { LeaveApprovalStatusBadge } from "./StatusBadge";
import LeaveRequestFilters from "./LeaveRequestFilters";
import {
  EMPTY_LEAVE_REQUEST_FILTERS,
  type LeaveRequestFilterValues,
} from "./LeaveRequestFilters.constants";
import {
  LEAVE_TYPE_OPTIONS,
  filterLeaveApprovals,
  formatDateDmy,
  formatDateYmd,
  leaveTypeLabel,
  normalizeLeaveStatus,
  resolveName,
} from "./utils";

type EmployeeOption = { value: string; label: string };

type FormState = {
  start_date: string;
  end_date: string;
  leave_type: LeaveType;
  requested_by: string;
  reason: string;
};

const emptyForm = (): FormState => ({
  start_date: "",
  end_date: "",
  leave_type: "full-day",
  requested_by: getEmployeeId() ?? "",
  reason: "",
});

function toDateInputValue(value?: string | null): string {
  if (!value) {
    return "";
  }
  const ymd = formatDateYmd(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : "";
}

type EmployeeTabProps = {
  employeeNameById: Map<string, string>;
  employeeOptions: EmployeeOption[];
};

const EmployeeTab = ({
  employeeNameById,
  employeeOptions,
}: EmployeeTabProps) => {
  const queryClient = useQueryClient();
  const formCardRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<LeaveRequestFilterValues>(
    EMPTY_LEAVE_REQUEST_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues]);

  const isEditMode = !!editingId;

  const currentEmployeeId = getEmployeeId() ?? "";
  const canViewAllEmployeeRequests = isPTSAdmin();
  const requestedByFilter = canViewAllEmployeeRequests
    ? undefined
    : currentEmployeeId || undefined;

  const { data, isLoading } = useQuery({
    queryKey: [
      "leave-approvals",
      "employee-list",
      canViewAllEmployeeRequests ? "all" : currentEmployeeId,
      filterValues.startDate,
      filterValues.endDate,
      filterValues.leaveType,
      filterValues.status,
      filterValues.search,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsLeaveApprovalService.getLeaveApprovals({
        page: currentPage,
        page_size: pageSize,
        start_date: filterValues.startDate,
        end_date: filterValues.endDate,
        leave_type: filterValues.leaveType,
        status: filterValues.status,
        search: filterValues.search,
        requested_by: requestedByFilter,
      }),
    enabled: canViewAllEmployeeRequests || !!currentEmployeeId,
  });

  const recentRequests = data?.results;
  const totalCount = data?.count ?? 0;

  const filteredRequests = useMemo(
    () =>
      filterLeaveApprovals(recentRequests ?? [], {
        leaveType: filterValues.leaveType,
        status: filterValues.status,
        search: filterValues.search,
      }),
    [recentRequests, filterValues]
  );

  const handleExportCSV = async () => {
    try {
      const res = await PtsLeaveApprovalService.getLeaveApprovals({
        page_size: 100000,
        start_date: filterValues.startDate,
        end_date: filterValues.endDate,
        leave_type: filterValues.leaveType,
        status: filterValues.status,
        search: filterValues.search,
        requested_by: requestedByFilter,
      });

      const items = res.results;

      const escapeCsv = (val: unknown) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "Start Date",
        "End Date",
        "Leave Type",
        "Reason",
        "Requested By Name",
        "Status",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const reqName =
          item.requested_by_name ??
          resolveName(item.requested_by, employeeNameById);

        return [
          escapeCsv(formatDateDmy(item.start_date)),
          escapeCsv(formatDateDmy(item.end_date)),
          escapeCsv(leaveTypeLabel(item.leave_type)),
          escapeCsv(item.reason),
          escapeCsv(reqName),
          escapeCsv(item.status),
          escapeCsv(formatDateDmy(item.created_on)),
          escapeCsv(formatDateDmy(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `leave-requests-employee-${formatDateYmd(new Date().toISOString())}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
  };

  const saveMutation = useMutation<LeaveApprovalItem | undefined, Error>({
    mutationFn: async () => {
      if (editingId) {
        return PtsLeaveApprovalService.updateLeaveApprovalForm(editingId, form);
      }
      return PtsLeaveApprovalService.createLeaveApproval(form);
    },
    onSuccess: result => {
      if (!result?.id) {
        return;
      }
      notify.success(
        isEditMode
          ? "Leave request updated successfully"
          : "Leave request submitted for approval"
      );
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["leave-approvals"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => PtsLeaveApprovalService.deleteLeaveApproval(id),
    onSuccess: (_result, id) => {
      notify.success("Leave request deleted successfully");
      if (editingId === id) {
        resetForm();
      }
      queryClient.invalidateQueries({ queryKey: ["leave-approvals"] });
    },
  });

  const validate = (): string | null => {
    if (!form.start_date) {
      return "Please select a start date";
    }
    if (!form.end_date) {
      return "Please select an end date";
    }
    if (form.end_date < form.start_date) {
      return "End date cannot be before start date";
    }
    if (!form.leave_type) {
      return "Please select a leave type";
    }
    if (!form.requested_by) {
      return "Please select who is requesting leave";
    }
    if (!form.reason.trim()) {
      return "Please enter a reason";
    }
    return null;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error) {
      notify.error(error);
      return;
    }
    saveMutation.mutate();
  };

  const handleEdit = (item: LeaveApprovalItem) => {
    const leaveType = LEAVE_TYPE_OPTIONS.some(
      option => option.value === item.leave_type
    )
      ? item.leave_type
      : "full-day";

    setEditingId(item.id);
    setForm({
      start_date: toDateInputValue(item.start_date),
      end_date: toDateInputValue(item.end_date),
      leave_type: leaveType,
      requested_by: String(item.requested_by || currentEmployeeId),
      reason: item.reason ?? "",
    });
    formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Leave requests
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleExportCSV}
            className="h-10 px-5"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card ref={formCardRef} className="border-gray-200 bg-white shadow-sm">
        <CardContent className="space-y-5 p-5 md:p-6">
          <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-[#111827]">
                {isEditMode ? "Edit leave request" : "Raise leave request"}
              </h2>
              <Info className="h-4 w-4 text-gray-400" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isEditMode ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={saveMutation.isPending}
                  className="h-10 px-5"
                >
                  Cancel
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={saveMutation.isPending}
                className="bg-primary hover:bg-primary/90 h-10 px-5 text-white"
              >
                {saveMutation.isPending
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                    ? "Update"
                    : "Submit for approval"}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="space-y-1.5 md:col-span-3">
              <Label className="text-sm text-[#374151]">
                Start Date <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      start_date: e.target.value,
                    }))
                  }
                  className="h-10 bg-white pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="space-y-1.5 md:col-span-3">
              <Label className="text-sm text-[#374151]">
                End Date <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                  className="h-10 bg-white pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="space-y-1.5 md:col-span-3">
              <Label className="text-sm text-[#374151]">
                Leave Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.leave_type}
                onValueChange={value =>
                  setForm(prev => ({
                    ...prev,
                    leave_type: value as LeaveType,
                  }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select Leave Type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LEAVE_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-3">
              <Label className="text-sm text-[#374151]">
                Requested By <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.requested_by || undefined}
                disabled
                onValueChange={value =>
                  setForm(prev => ({ ...prev, requested_by: value }))
                }
              >
                <SelectTrigger className="h-10 cursor-not-allowed bg-gray-50 text-gray-500 opacity-80">
                  <SelectValue placeholder="Select Requester" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {employeeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex h-full flex-col space-y-1.5 md:col-span-12">
              <Label className="text-sm text-[#374151]">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Provide a reason for the leave request..."
                value={form.reason}
                onChange={e =>
                  setForm(prev => ({ ...prev, reason: e.target.value }))
                }
                className="min-h-[100px] flex-1 resize-none bg-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <LeaveRequestFilters
        values={filterValues}
        onChange={setFilterValues}
        idPrefix="employee-leave-request"
      />

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="px-5 py-3 font-medium">Start Date</th>
                  <th className="px-5 py-3 font-medium">End Date</th>
                  <th className="px-5 py-3 font-medium">Leave Type</th>
                  <th className="px-5 py-3 font-medium">Reason</th>
                  <th className="px-5 py-3 font-medium">Requested By</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Created On</th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading leave requests...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No leave requests found.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map(item => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 last:border-0 hover:bg-slate-50/50"
                    >
                      <td className="px-5 py-3.5 font-medium text-[#111827]">
                        {formatDateDmy(item.start_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.end_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {leaveTypeLabel(item.leave_type)}
                      </td>
                      <td className="max-w-[260px] truncate px-5 py-3.5 text-[#374151]">
                        {item.reason ?? "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.requested_by_name ??
                          resolveName(item.requested_by, employeeNameById)}
                      </td>
                      <td className="px-5 py-3.5">
                        <LeaveApprovalStatusBadge status={item.status} />
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.created_on)}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(() => {
                            const status = normalizeLeaveStatus(item.status);
                            const isEditable = status === "pending";
                            const isDeletable = status === "pending";

                            return (
                              <>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  disabled={!isEditable}
                                  onClick={() => handleEdit(item)}
                                  className={cn(
                                    "text-primary h-8 w-8 hover:text-[#111827c2]",
                                    !isEditable &&
                                      "cursor-not-allowed opacity-40"
                                  )}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Popconfirm
                                  title="Delete Leave Request"
                                  description="Are you sure you want to delete this leave request?"
                                  disabled={!isDeletable}
                                  onConfirm={() =>
                                    deleteMutation.mutate(item.id)
                                  }
                                  okText="Yes"
                                  cancelText="No"
                                  okButtonProps={{
                                    loading: deleteMutation.isPending,
                                  }}
                                >
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={!isDeletable}
                                    className={cn(
                                      "h-8 w-8 text-red-600 hover:text-red-400",
                                      !isDeletable &&
                                        "cursor-not-allowed opacity-40"
                                    )}
                                  >
                                    <Trash className="h-4 w-4" />
                                  </Button>
                                </Popconfirm>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  if (size && size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                    return;
                  }
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={["50", "100", "200", "500"]}
                defaultPageSize={50}
                disabled={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeTab;
